import type { DocPageContent } from '../docs-content'

// order 1.5 places this right after Getting Started in the Start group.
const page = {
  order: 1.5,
  slug: 'concepts',
  title: '精剪的工作原理',
  description:
    '心智模型：本地优先存储、项目与链接媒体、帧与时码，以及 GPU 管道。',
  category: '入门',
  related: ['workspaces', 'getting-started', 'editing-tools'],
  sections: [
    {
      title: '本地优先，设计如此',
      blocks: [
        {
          kind: 'list',
          items: [
            '精剪在你的浏览器中运行，并将一切存储在磁盘上的**工作区文件夹**中。',
            '你的原始视频与音频是**链接**的，而非被复制或上传——精剪指向它们已有的文件位置。',
            '项目、缓存、生成的素材与导出都会写入工作区，因此你可以像对待任何文件夹一样备份或移动它。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '由于媒体是链接的，移动或重命名源文件会显示为**缺失媒体**，直到你重新链接为止。参见 [工作区与存储](workspaces)。',
        },
      ],
    },
    {
      title: '项目、轨道与片段',
      blocks: [
        {
          kind: 'list',
          items: [
            '一个**项目**包含一条时间轴、其设置（分辨率与帧率），以及对你所用媒体的引用。',
            '时间轴层叠**轨道**；较高的轨道渲染在较低的轨道之上。',
            '时间轴上的**片段**是通向源文件的一扇窗口——修剪片段只会改变源文件播放的部分，而不会改动文件本身。',
            '**调整图层**是一种本身不含媒体的片段——它会将其特效与色彩应用于其下方的每个片段。',
          ],
        },
      ],
    },
    {
      title: '序列与复合片段',
      blocks: [
        {
          kind: 'paragraph',
          text: '精剪用一个基本单元来表示**项目内的时间轴**——那就是合成体（composition）。它会根据你使用方式的不同而以两种面貌出现。',
        },
        {
          kind: 'list',
          items: [
            '**运动合成体**在**运动**中打开，共享编辑器会保留你的媒体、预览与属性，而下方的停靠区变为一个图层时间轴。',
            '**序列**是同一项目中一个独立的时间轴，从标签栏的标签页打开。每个序列都有自己的轨道、片段与编辑历史——主时间轴本身就是一个序列。',
            '**复合片段**将时间轴中的一段折叠为单个可复用的媒体项。在时间轴上它表现得像一个片段——可移动、修剪、添加特效——而其内容存在于它自己的时间轴中。',
            '两者本质上是同一事物的不同视角：打开复合片段可将其内容作为序列编辑，而任何序列都可以作为复合片段拖入另一个时间轴。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '合成体可以包含其他合成体，但绝不会包含自身——精剪会阻止循环引用，因此复合片段永远不会嵌套进自己的内容里。',
        },
      ],
    },
    {
      title: '帧与时码',
      blocks: [
        {
          kind: 'list',
          items: [
            '时序以**帧**为单位计量。一个片段在给定项目帧率下拥有起始帧与帧长度。',
            '预览会显示**时码**；在某些视图中点击读数即可切换到帧编号。',
            '源媒体的帧率可能与项目不同，因此精剪在编辑时会将源帧转换为项目帧。',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '创建项目时，请将项目分辨率与帧率设置为匹配你的交付目标。',
        },
      ],
    },
    {
      title: '一切都在你的机器上渲染',
      blocks: [
        {
          kind: 'list',
          items: [
            '播放、特效、调色与 AI 工具都使用你的 GPU（WebGPU）和 CPU（WebCodecs）在本地运行。',
            '**代理**是重媒体的轻量副本，能使编辑更流畅；最终导出始终使用原始文件。',
            '由于没有云端渲染，导出期间保持标签页打开很重要——渲染就发生在浏览器中。',
          ],
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
